/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { Epic } from '../../types';
import { ChevronDown, ChevronRight, BookOpen, Sparkles, Loader, Pencil, X, Check } from 'lucide-react';

interface EpicCardProps {
  epic: Epic;
  isExpanded: boolean;
  storyCount: number;
  isGrooming?: boolean;
  isEditing?: boolean;
  isUpdating?: boolean;
  editedFields?: Partial<Epic>;
  statusOptions: string[];
  onToggleExpansion: () => void;
  onGroomEpic?: () => void;
  onStartEditing?: () => void;
  onCancelEditing?: () => void;
  onSaveEdits?: () => void;
  onFieldChange?: (field: keyof Epic, value: any) => void;
  onStatusUpdate?: (newStatus: string) => void;
  getStatusColor: (status: string) => string;
}

export function EpicCard({ 
  epic, 
  isExpanded, 
  storyCount,
  isGrooming = false,
  isEditing = false,
  isUpdating = false,
  editedFields = {},
  statusOptions,
  onToggleExpansion,
  onGroomEpic,
  onStartEditing,
  onCancelEditing,
  onSaveEdits,
  onFieldChange,
  onStatusUpdate,
  getStatusColor 
}: EpicCardProps) {
  const statusColor = epic.status ? getStatusColor(epic.status) : '#6c757d';

  const handleGroomClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onGroomEpic && !isGrooming) {
      onGroomEpic();
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onStartEditing) {
      onStartEditing();
    }
  };

  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onCancelEditing) {
      onCancelEditing();
    }
  };

  const handleSaveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSaveEdits) {
      onSaveEdits();
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation();
    if (onStatusUpdate) {
      onStatusUpdate(e.target.value);
    }
  };

  const handleFieldChange = (field: keyof Epic, value: any) => {
    if (onFieldChange) {
      onFieldChange(field, value);
    }
  };

  const displayTitle = isEditing ? (editedFields.title ?? epic.title) : epic.title;
  const displayDescription = isEditing ? (editedFields.description ?? epic.description) : epic.description;

  return (
    <div
      id={epic.id}
      style={{
        border: isEditing ? '2px solid #0066cc' : '2px solid #e0e0e0',
        borderRadius: '8px',
        padding: '16px',
        backgroundColor: isEditing ? '#f0f7ff' : '#f8f9fa',
        marginBottom: '12px',
        cursor: isEditing ? 'default' : 'pointer',
        transition: 'all 0.2s ease, background-color 0.5s'
      }}
      onClick={isEditing ? undefined : onToggleExpansion}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', flex: 1 }}>
          <div style={{ marginRight: '12px', marginTop: '2px' }}>
            {isExpanded ? (
              <ChevronDown size={20} color="#666" />
            ) : (
              <ChevronRight size={20} color="#666" />
            )}
          </div>
          <BookOpen size={20} color="#0066cc" style={{ marginRight: '12px', marginTop: '2px' }} />
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
              {isEditing ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                  <span style={{ fontWeight: 600, color: '#333' }}>{epic.id}:</span>
                  <input
                    type="text"
                    value={displayTitle}
                    onChange={(e) => handleFieldChange('title', e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      flex: 1,
                      padding: '6px 10px',
                      fontSize: '16px',
                      fontWeight: 600,
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                    }}
                  />
                </div>
              ) : (
                <h3 style={{ 
                  margin: 0, 
                  fontSize: '18px', 
                  fontWeight: 600,
                  color: '#333'
                }}>
                  {epic.id}: {epic.title}
                </h3>
              )}
              {isEditing ? (
                <select
                  value={editedFields.status ?? epic.status ?? 'Not Started'}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleFieldChange('status', e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    padding: '4px 8px',
                    borderRadius: '6px',
                    border: '1px solid #ccc',
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    backgroundColor: 'white',
                  }}
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              ) : epic.status && (
                <span
                  style={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 500,
                    backgroundColor: statusColor,
                    color: 'white',
                  }}
                >
                  {epic.status}
                </span>
              )}
              <span
                style={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 500,
                  backgroundColor: '#6c757d',
                  color: 'white',
                }}
              >
                {storyCount} {storyCount === 1 ? 'story' : 'stories'}
              </span>
            </div>
            {(isExpanded || isEditing) && (
              <div style={{ marginTop: '12px' }}>
                {isEditing ? (
                  <textarea
                    value={displayDescription}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      fontSize: '14px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      resize: 'vertical',
                      lineHeight: '1.5',
                    }}
                  />
                ) : (
                  <p style={{ 
                    margin: 0, 
                    color: '#666',
                    lineHeight: '1.5'
                  }}>
                    {epic.description}
                  </p>
                )}
                {epic.section && !isEditing && (
                  <div style={{ marginTop: '8px', fontSize: '13px', color: '#888' }}>
                    <strong>Section:</strong> {epic.section}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', marginLeft: '12px' }}>
          {isEditing ? (
            <>
              <button
                onClick={handleSaveClick}
                disabled={isUpdating}
                title="Save changes"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: isUpdating ? '#e0e0e0' : '#28a745',
                  color: isUpdating ? '#666' : 'white',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  if (!isUpdating) {
                    e.currentTarget.style.backgroundColor = '#218838';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isUpdating) {
                    e.currentTarget.style.backgroundColor = '#28a745';
                  }
                }}
              >
                {isUpdating ? (
                  <>
                    <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={14} />
                    Save
                  </>
                )}
              </button>
              <button
                onClick={handleCancelClick}
                disabled={isUpdating}
                title="Cancel editing"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: isUpdating ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  if (!isUpdating) {
                    e.currentTarget.style.backgroundColor = '#5a6268';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isUpdating) {
                    e.currentTarget.style.backgroundColor = '#6c757d';
                  }
                }}
              >
                <X size={14} />
                Cancel
              </button>
            </>
          ) : (
            <>
              {onStartEditing && (
                <button
                  onClick={handleEditClick}
                  title="Edit epic"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: '#0066cc',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#0052a3';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#0066cc';
                  }}
                >
                  <Pencil size={14} />
                  Edit
                </button>
              )}
              {onGroomEpic && (
                <button
                  onClick={handleGroomClick}
                  disabled={isGrooming}
                  title={isGrooming ? 'Grooming epic...' : 'Groom this epic into implementation issues'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    backgroundColor: isGrooming ? '#e0e0e0' : '#8b5cf6',
                    color: isGrooming ? '#666' : 'white',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: isGrooming ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => {
                    if (!isGrooming) {
                      e.currentTarget.style.backgroundColor = '#7c3aed';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isGrooming) {
                      e.currentTarget.style.backgroundColor = '#8b5cf6';
                    }
                  }}
                >
                  {isGrooming ? (
                    <>
                      <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      Grooming...
                    </>
                  ) : (
                    <>
                      <Sparkles size={14} />
                      Groom
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

